/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from "react";
import ConstraintCard from "./ConstraintCard";

/**
 * RowLimitBuilder — caps how many rows may exist per group.
 *
 * Props:
 *   value            — array of { name, scope_field, where?, limit_value? |
 *                      (limit_table, limit_match_field, limit_field) }
 *   availableColumns — array of { name, data_type }
 *   allTables        — array of { name, fields: [{name}] } for the lookup form
 *
 * Two ways to say how many are allowed:
 *   a fixed number  — every group gets the same cap
 *   read from a parent row — each group carries its own, and blank means unlimited
 */
export default function RowLimitBuilder({
  value = [], onChange, availableColumns = [], allTables = [], hasRows = false, onEditingChange,
}) {
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (onEditingChange) onEditingChange(!!editing); }, [editing, onEditingChange]);

  const WHERE_BANNED = [[";", "a semicolon"], ["--", "a SQL comment"], ["/*", "a block comment"]];
  function whereError(w) {
    const t = (w || "").trim();
    if (!t) return "";
    for (const [tok, described] of WHERE_BANNED) {
      if (t.includes(tok)) return `Remove ${described} — this takes a single condition.`;
    }
    return "";
  }

  const d = editing && editing.data;
  const usesLookup = d && d.mode === "lookup";
  const valid = d
    && d.name.trim()
    && d.scope_field
    && !whereError(d.where)
    && (usesLookup
      ? (d.limit_table && d.limit_match_field && d.limit_field)
      : (String(d.limit_value).trim() !== "" && Number(d.limit_value) >= 0));

  const saveHint = "Give it a name, choose the grouping field, and say how many are allowed.";

  function blank() {
    return { name: "", scope_field: "", where: "", mode: "fixed", limit_value: "",
             limit_table: "", limit_match_field: "", limit_field: "" };
  }
  function startNew() { setEditing({ index: "new", data: blank() }); }
  function startEdit(idx) {
    const row = value[idx] || {};
    setEditing({ index: idx, data: {
      ...blank(), ...row,
      mode: row.limit_table ? "lookup" : "fixed",
      limit_value: row.limit_value ?? "",
    }});
  }
  function cancel() { setEditing(null); }
  function remove(idx) { onChange(value.filter((_, i) => i !== idx)); }
  function patch(p) { setEditing(e => ({ ...e, data: { ...e.data, ...p } })); }

  function save() {
    if (!valid) return;
    const row = { name: d.name.trim(), scope_field: d.scope_field };
    if ((d.where || "").trim()) row.where = d.where.trim();
    if (usesLookup) {
      row.limit_table = d.limit_table;
      row.limit_match_field = d.limit_match_field;
      row.limit_field = d.limit_field;
    } else {
      row.limit_value = Number(d.limit_value);
    }
    if (editing.index === "new") onChange([...value, row]);
    else onChange(value.map((r, i) => i === editing.index ? row : r));
    setEditing(null);
  }

  const parentColumns = (allTables.find(t => t.name === (d && d.limit_table)) || {}).fields || [];
  const sel = { fontSize: 13 };
  const lbl = { fontSize: 12, color: "#555" };

  const editorBody = editing && (
    <>
      <div className="mb-2">
        <label style={lbl}>Name</label>
        <input className="form-control" style={sel} placeholder="e.g. cap_registrations_per_event"
          value={d.name} onChange={e => patch({ name: e.target.value })} />
      </div>

      <div className="mb-2">
        <label style={lbl}>Count rows grouped by</label>
        <select className="form-select" style={sel} value={d.scope_field}
          onChange={e => patch({ scope_field: e.target.value })}>
          <option value="">— choose a field —</option>
          {availableColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
          The cap applies separately to each value of this field — so each event, each class,
          each slot gets its own allowance.
        </div>
      </div>

      <div className="mb-2">
        <label style={lbl}>How many are allowed</label>
        <div className="d-flex gap-3 mb-2" style={{ fontSize: 13 }}>
          <label style={{ cursor: "pointer" }}>
            <input type="radio" checked={!usesLookup} onChange={() => patch({ mode: "fixed" })} />{" "}
            A fixed number
          </label>
          <label style={{ cursor: "pointer" }}>
            <input type="radio" checked={usesLookup} onChange={() => patch({ mode: "lookup" })} />{" "}
            Read it from another table
          </label>
        </div>

        {!usesLookup ? (
          <input type="number" min="0" className="form-control" style={{ ...sel, maxWidth: 160 }}
            placeholder="e.g. 100" value={d.limit_value}
            onChange={e => patch({ limit_value: e.target.value })} />
        ) : (
          <div className="d-flex gap-2 flex-wrap">
            <div style={{ minWidth: 210 }}>
              <label style={lbl}>Table holding the limit</label>
              <select className="form-select" style={sel} value={d.limit_table}
                onChange={e => patch({ limit_table: e.target.value, limit_match_field: "", limit_field: "" })}>
                <option value="">— choose —</option>
                {allTables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 190 }}>
              <label style={lbl}>Matched on</label>
              <select className="form-select" style={sel} value={d.limit_match_field}
                onChange={e => patch({ limit_match_field: e.target.value })} disabled={!d.limit_table}>
                <option value="">— choose —</option>
                {parentColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 190 }}>
              <label style={lbl}>Number is in</label>
              <select className="form-select" style={sel} value={d.limit_field}
                onChange={e => patch({ limit_field: e.target.value })} disabled={!d.limit_table}>
                <option value="">— choose —</option>
                {parentColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 11, color: "#888", width: "100%" }}>
              Each group reads its own cap from that row. Leave the number empty there and that
              group is unlimited.
            </div>
          </div>
        )}
      </div>

      <div>
        <label style={lbl}>Only count rows where <span style={{ color: "#888" }}>(optional)</span></label>
        <input className="form-control" style={{ ...sel, fontFamily: "monospace" }}
          placeholder="e.g. status = 'CONFIRMED'"
          value={d.where || ""} onChange={e => patch({ where: e.target.value })} />
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
          <i className="bi bi-info-circle" /> Without this, every row occupies a place — including
          cancelled ones, so a place freed by a cancellation would never reopen.
        </div>
        {whereError(d.where) && (
          <div style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>
            <i className="bi bi-exclamation-triangle" /> {whereError(d.where)}
          </div>
        )}
      </div>
    </>
  );

  function summaryOf(row) {
    const cap = row.limit_table
      ? `${row.limit_table}.${row.limit_field}`
      : row.limit_value;
    return `at most ${cap} per ${row.scope_field}` + (row.where ? `, counting only ${row.where}` : "");
  }

  return (
    <div>
      {value.map((row, idx) => (
        editing && editing.index === idx ? (
          <ConstraintCard key={idx} mode="edit" title="Edit Row Limit"
            onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
            {editorBody}
          </ConstraintCard>
        ) : (
          <ConstraintCard key={idx} mode="summary" title={row.name || "(unnamed)"}
            canEdit={!editing}
            onEdit={() => startEdit(idx)} onRemove={() => remove(idx)}
            summary={<code style={{ fontSize: 12, color: "#555" }}>{summaryOf(row)}</code>} />
        )
      ))}

      {editing && editing.index === "new" && (
        <ConstraintCard mode="edit" title="New Row Limit"
          onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
          {editorBody}
        </ConstraintCard>
      )}

      {!editing && (
        <button type="button" onClick={startNew}
          style={{ fontSize: 13, color: "#0d6efd", background: "none", border: "1px solid #0d6efd",
                   borderRadius: 6, padding: "4px 14px", cursor: "pointer", marginTop: 4 }}>
          <i className="bi bi-plus-lg" /> Add Row Limit
        </button>
      )}
    </div>
  );
}
